/**
 * Web-panel strings not (yet) present in `@nexops/shared` messages.
 * Merged over the shared dictionaries at runtime. Keep `pl` and `en` in sync — the `en`
 * object is typed against the `pl` shape, so a missing key is a compile error.
 * Candidates for promotion to `packages/shared` once the mobile app needs them.
 */
import { analyticsEn, analyticsPl } from './analytics';
import { c360En, c360Pl } from './customer360';
import { custViewsEn, custViewsPl } from './customers-views';
import { listViewsEn, listViewsPl } from './list-views';
import { custDeliveriesEn, custDeliveriesPl } from './customer-deliveries';
import { logisticsEn, logisticsPl } from './logistics';
import { logOptEn, logOptPl } from './logistics-optimization';
import { logFulEn, logFulPl } from './logistics-fulfilment';
import { leadsEn, leadsPl } from './leads';
import { workflowEn, workflowPl } from './workflow';
import { wfdEn, wfdPl } from './workflow-decision';
import { ccardEn, ccardPl } from './customer-card';
import { odEn, odPl } from './owner-dashboard';
import { layoutEn, layoutPl } from './layout';
import { myDayEn, myDayPl } from './my-day';
import { tasksEn, tasksPl } from './tasks';
import { visitsEn, visitsPl } from './visits';
import { dayTeamEn, dayTeamPl } from './day-team';
import { dailySheetEn, dailySheetPl } from './daily-sheet';
import { gdprPageEn, gdprPagePl } from './gdpr';
import { dictEn, dictPl } from './dictionaries';
import { alertsEn, alertsPl, notifEn, notifPl } from './alerts';
import { aiEn, aiPl } from './ai';
import { opsEn, opsPl } from './analytics-ops';
import { finEn, finPl } from './analytics-finance';
import { acpEn, acpPl } from './analytics-cp';
import { heroEn, heroPl } from './hero';
import { searchEn, searchPl } from './search';
import { chatEn, chatPl } from './chat';
import { heroSalesEn, heroSalesPl } from './hero-sales';
import { heroWorkEn, heroWorkPl } from './hero-work';
import { heroBiEn, heroBiPl } from './hero-bi';
import { heroFinEn, heroFinPl } from './hero-fin';
import { heroAdminEn, heroAdminPl } from './hero-admin';
import { admEn, admPl } from './admin';
import { audEn, audPl } from './audit';
import { lxEn, lxPl } from './live-extra';
import { lhistEn, lhistPl } from './live-history';
import { notifsEn, notifsPl } from './notifications-center';
import { appuEn, appuPl } from './app-update';
import { fleetWebEn, fleetWebPl } from './fleet';
import { fleetReportsEn, fleetReportsPl } from './fleet-reports';
import { fleetAnalyticsEn, fleetAnalyticsPl } from './fleet-analytics';
import { fleetBoardEn, fleetBoardPl } from './fleet-board';
import { fleetKitEn, fleetKitPl } from './fleet-kit';
import { fleetAnalyticsViewsEn, fleetAnalyticsViewsPl } from './fleet-analytics-views';
import { fleetRoutesEn, fleetRoutesPl } from './fleet-routes';
import { fleetAnalyticsUiEn, fleetAnalyticsUiPl } from './fleet-analytics-ui';
import { fleetAiEn, fleetAiPl } from '@/features/fleet/ai/fleet-ai.i18n';
import { fleetReviewUiEn, fleetReviewUiPl } from './fleet-review-ui';
import { fleetLogbooksUiEn, fleetLogbooksUiPl } from './fleet-logbooks-ui';
import { fleetChecklistsServiceUiEn, fleetChecklistsServiceUiPl } from './fleet-checklists-service-ui';
import { fleetLeasesUiEn, fleetLeasesUiPl } from './fleet-leases-ui';
import { fleetVehiclesUiEn, fleetVehiclesUiPl } from './fleet-vehicles-ui';
import { acctEn, acctPl } from './account';
import { repSalesEn, repSalesPl } from '@/features/rep-sales/rep-sales.i18n';
import { repSalesWeekEn, repSalesWeekPl } from '@/features/rep-sales/rep-sales-week.i18n';
import { mbrEn, mbrPl } from './mbr';
import { insEn, insPl } from './insurance';
import { erpsEn, erpsPl } from './erp-sync';
import { plnEn, plnPl } from './planner';
import { collEn, collPl } from './collections';
import { cdocEn, cdocPl } from './customer-documents';
import { foreignCustEn, foreignCustPl } from './foreign-customers';
import { custLifeEn, custLifePl } from './customer-lifecycle';
import { costsEn, costsPl } from './costs';
import { cfEn, cfPl } from './cashflow';
import { purEn, purPl } from './purchasing';
import { qualEn, qualPl } from './quality';
import { comEn, comPl } from './commissions';
import { comOvEn, comOvPl } from './commissions-overview';
import { terenEn, terenPl } from './teren';
import { whEn, whPl } from './warehouse';
import { svyEn, svyPl } from './surveys';
import { prcEn, prcPl } from './pricing';
import { ivcEn, ivcPl } from './invoice-compare';
import { sordEn, sordPl } from './sales-orders';
import { lvPl } from './leave';
import { lvEn } from './leave-en';

type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const extraPl = {
  c360: c360Pl,
  custViews: custViewsPl,
  listViews: listViewsPl,
  custDeliveries: custDeliveriesPl,
  logistics: logisticsPl,
  logOpt: logOptPl,
  logFul: logFulPl,
  analytics: analyticsPl,
  leads: leadsPl,
  workflow: workflowPl,
  wfd: wfdPl,
  ccard: ccardPl,
  od: odPl,
  layout: layoutPl,
  tasks: tasksPl,
  visitsPage: visitsPl,
  dayTeam: dayTeamPl,
  dailySheet: dailySheetPl,
  gdprPage: gdprPagePl,
  dict: dictPl,
  adm: admPl,
  aud: audPl,
  lhist: lhistPl,
  alerts: alertsPl,
  notif: notifPl,
  notifs: notifsPl,
  appu: appuPl,
  fleet: { web: { ...fleetWebPl, ...fleetReportsPl, ...fleetAnalyticsPl, ...fleetBoardPl, ...fleetKitPl, ...fleetVehiclesUiPl, ...fleetLeasesUiPl, ...fleetAnalyticsViewsPl, ...fleetRoutesPl, ...fleetAnalyticsUiPl, ...fleetLogbooksUiPl, ...fleetReviewUiPl, ...fleetChecklistsServiceUiPl, ai: fleetAiPl } },
  acct: acctPl,
  ai: aiPl,
  hero: heroPl,
  search: searchPl,
  chat: chatPl,
  heroSales: heroSalesPl,
  heroWork: heroWorkPl,
  heroBi: heroBiPl,
  heroFin: heroFinPl,
  heroAdmin: heroAdminPl,
  ops: opsPl,
  fin: finPl,
  acp: acpPl,
  lx: lxPl,
  repSales: repSalesPl,
  repSalesWeek: repSalesWeekPl,
  mbr: mbrPl,
  ins: insPl,
  erps: erpsPl,
  pln: plnPl,
  coll: collPl,
  cdoc: cdocPl,
  foreignCust: foreignCustPl,
  custLife: custLifePl,
  costs: costsPl,
  cf: cfPl,
  pur: purPl,
  qual: qualPl,
  com: comPl,
  comov: comOvPl,
  teren: terenPl,
  wh: whPl,
  svy: svyPl,
  prc: prcPl,
  ivc: ivcPl,
  sord: sordPl,
  lv: lvPl,
  nav: {
    main: 'Nawigacja główna',
    analyticsBoard: 'Zarząd',
    analyticsTeam: 'Zespół',
    analyticsCustomers: 'Klienci',
    analyticsProducts: 'Produkty',
    analyticsMargin: 'Marża',
    analyticsForecasts: 'Prognozy',
    analyticsReceivables: 'Należności',
    analyticsProfitability: 'Rentowność',
    collapse: 'Zwiń menu',
    expand: 'Rozwiń menu',
    openMenu: 'Otwórz menu',
    legal: 'Centrum prawne',
    gdpr: 'Dane osobowe (RODO)',
    consents: 'Zgody prawne',
    users: 'Użytkownicy',
    roles: 'Role i uprawnienia',
    settings: 'Ustawienia',
    dictionaries: 'Słowniki',
    audit: 'Audyt log',
    feedback: 'Zgłoszenia',
    help: 'Pomoc',
  },
  common: {
    retry: 'Spróbuj ponownie',
    back: 'Wstecz',
    clear: 'Wyczyść',
    close: 'Zamknij',
    all: 'Wszystkie',
    columns: 'Kolumny',
    rowsPerPage: 'Wierszy na stronie',
    pageOf: 'Strona {page} z {pages}',
    results: '{count} wyników',
    sampleData: 'Dane przykładowe',
    comingSoon: 'W przygotowaniu',
    list: 'Lista',
    map: 'Mapa',
    previous: 'Poprzednia',
    next: 'Następna',
    errorGeneric: 'Coś poszło nie tak. Spróbuj ponownie.',
    errorNetwork: 'Brak połączenia z serwerem.',
    errorForbidden: 'Brak uprawnień do tej operacji.',
    noResults: 'Brak wyników',
    unknown: '—',
  },
  auth: {
    title: 'Zaloguj się',
    subtitle: 'Zaloguj się do panelu {client}',
    signingIn: 'Logowanie…',
    totpLabel: 'Kod weryfikacyjny',
    totpHint: 'Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej.',
    invalidCredentials: 'Nieprawidłowy e-mail lub hasło.',
    invalidTotp: 'Nieprawidłowy kod weryfikacyjny.',
    tooManyAttempts: 'Zbyt wiele prób. Spróbuj za chwilę.',
    emailInvalid: 'Podaj poprawny adres e-mail.',
    passwordRequired: 'Podaj hasło.',
    totpFormat: 'Kod ma 6 cyfr.',
    useOtherAccount: 'Użyj innego konta',
    sessionExpired: 'Sesja wygasła. Zaloguj się ponownie.',
    idleLoggedOut: 'Wylogowano z powodu braku aktywności.',
    idleTitle: 'Sesja wkrótce wygaśnie',
    idleBody: 'Ze względów bezpieczeństwa zostaniesz wylogowany za {seconds} s.',
    stayLoggedIn: 'Pozostań zalogowany',
    logoutNow: 'Wyloguj teraz',
    poweredBy: 'Platforma NEXOPS',
    heading: 'Logowanie do systemu',
    headingSub: 'Wprowadź dane służbowe, aby uzyskać dostęp do panelu {client}.',
    emailLabel: 'Identyfikator służbowy',
    emailPlaceholder: 'jan.kowalski@firma.pl',
    passwordLabel: 'Hasło dostępu',
    passwordPlaceholder: 'Wprowadź hasło',
    showPassword: 'Pokaż hasło',
    hidePassword: 'Ukryj hasło',
    remember: 'Zapamiętaj mój e-mail na tym urządzeniu',
    submit: 'Zaloguj się do systemu',
    forgot: 'Nie pamiętasz hasła?',
    forgotBody: 'Reset hasła wykonuje administrator Twojej organizacji. Napisz do wsparcia, podając służbowy adres e-mail — ustawimy nowe hasło.',
    forgotAction: 'Poproś o reset hasła',
    capsLock: 'Caps Lock jest włączony',
    needHelp: 'Potrzebujesz pomocy?',
    writeToUs: 'Napisz do nas',
    callSupport: 'Zadzwoń do wsparcia',
    supportHours: 'pon–pt 7:00–18:00',
    secureTitle: 'Twoje dane są bezpieczne.',
    secureBody: 'Stosujemy szyfrowanie TLS, a każde działanie jest rejestrowane w dzienniku audytu.',
    systemName: 'NEXOPS — Platforma Operacji Biznesowych',
  },
  demo: {
    mode: 'Tryb demo',
    switchAs: 'Zaloguj jako…',
    hint: 'Przełącz rolę, aby zobaczyć, jak zmienia się zakres danych.',
    switched: 'Przełączono na: {name}',
    current: 'Bieżący',
  },
  dataScope: {
    OWN: 'Własne rekordy',
    TEAM: 'Zespół',
    ALL: 'Cała organizacja',
  },
  topbar: {
    search: 'Szukaj',
    searchPlaceholder: 'Szukaj klientów, leadów, zadań, wniosków…',
    pendingApprovals: 'do zatwierdzenia',
    returnedApprovals: 'do poprawy',
    help: 'Pomoc i skróty',
    helpIntro: 'Skróty klawiaturowe dostępne w całej aplikacji.',
    helpSearch: 'Wyszukiwarka',
    helpAssistant: 'Asystent AI',
    searchSoon: 'Wyszukiwanie globalne pojawi się w kolejnym etapie (M2).',
    notifications: 'Powiadomienia',
    noNotifications: 'Brak nowych powiadomień',
    userMenu: 'Menu użytkownika',
    language: 'Język',
    theme: 'Motyw',
    themeLight: 'Jasny',
    themeDark: 'Ciemny',
    themeSystem: 'Systemowy',
  },
  errors: {
    forbiddenTitle: 'Brak dostępu',
    forbiddenBody: 'Twoja rola nie ma uprawnień do tego widoku.',
    notFoundTitle: 'Nie znaleziono strony',
    notFoundBody: 'Strona, której szukasz, nie istnieje lub została przeniesiona.',
    goHome: 'Wróć do Mojego dnia',
  },
  placeholder: {
    title: 'W przygotowaniu — {milestone}',
    body: 'Ten moduł jest w budowie. Nawigacja i uprawnienia już działają.',
  },
  pages: {
    leads: 'Lejek pozyskania nowych klientów (kanban).',
    visits: 'Plan i historia wizyt handlowych.',
    tasks: 'Zadania i automatyczne kolejne kontakty.',
    workflow: 'Procesy akceptacji: nowy klient, ceny, limity.',
    analytics: 'Analityka sprzedaży, klientów i marży.',
    analyticsBoard: 'Dashboard zarządu: sprzedaż, marża, trendy.',
    analyticsTeam: 'Wyniki zespołu i realizacja targetów.',
    analyticsCustomers: 'ABC / Pareto, RFM, klienci utraceni i odzyskani.',
    analyticsProducts: 'Analiza produktów, cross-sell, white space.',
    analyticsMargin: 'Marża, korekty, waterfall.',
    analyticsForecasts: 'Prognozy sprzedaży i targety.',
    analyticsReceivables: 'Należności i struktura wiekowa.',
    analyticsProfitability: 'Rentowność klientów i tras.',
    logistics: 'Dostawy, trasy i koszty transportu.',
    admin: 'Użytkownicy, role, ustawienia organizacji, audyt.',
    legal: 'Dokumenty prawne, regulaminy i polityki.',
    gdpr: 'Eksport i usuwanie danych osobowych (RODO).',
    consents: 'Wersje i akceptacje zgód prawnych.',
    users: 'Konta użytkowników organizacji.',
    roles: 'Role, uprawnienia i zakresy danych.',
    settings: 'Ustawienia organizacji.',
    dictionaries: 'Listy wyboru w formularzach (np. dostawcy konkurencji).',
    audit: 'Dziennik zdarzeń audytowych.',
    feedback: 'Zgłoszenia błędów i sugestii.',
    help: 'Instrukcje i lista zmian.',
  },
  customers: {
    subtitle: 'Kartoteka klientów z danymi z ERP.',
    searchPlaceholder: 'Szukaj po nazwie, NIP, mieście…',
    emptyTitle: 'Brak klientów',
    emptyBody: 'Nie znaleziono klientów spełniających kryteria.',
    mapMissingCoords: '{count} klientów bez współrzędnych na tej stronie wyników',
    mapLimited: 'Na mapie {shown} z {total} klientów — zawęź wyszukiwanie lub filtry.',
    unassigned: 'Nieprzypisany',
    col: {
      name: 'Klient',
      nip: 'NIP',
      city: 'Miasto',
      status: 'Status',
      segment: 'Segment',
      customerClass: 'Klasa',
      owner: 'Opiekun',
      lastOrderAt: 'Ostatnie zamówienie',
      lastContactAt: 'Ostatni kontakt',
      nextContactAt: 'Następny kontakt',
    },
    detail: {
      backToList: 'Klienci',
      address: 'Adres',
      owner: 'Opiekun',
      nip: 'NIP',
      segment: 'Segment',
      lastContact: 'Ostatni kontakt',
      nextContact: 'Następny kontakt',
      notFound: 'Nie znaleziono klienta lub nie masz do niego dostępu.',
      tabSales: 'Sprzedaż',
      tabProducts: 'Produkty',
      tabVisits: 'Wizyty',
      tabDocuments: 'Dokumenty',
      tabAlerts: 'Alerty',
      tabPlaceholder: 'Sekcja „{tab}” pojawi się w etapie M2/M3.',
    },
  },
  myDay: myDayPl,
};

export type ExtraMessages = Shape<typeof extraPl>;

export const extraEn: ExtraMessages = {
  c360: c360En,
  custViews: custViewsEn,
  listViews: listViewsEn,
  custDeliveries: custDeliveriesEn,
  logistics: logisticsEn,
  logOpt: logOptEn,
  logFul: logFulEn,
  analytics: analyticsEn,
  leads: leadsEn,
  workflow: workflowEn,
  wfd: wfdEn,
  ccard: ccardEn,
  od: odEn,
  layout: layoutEn,
  tasks: tasksEn,
  visitsPage: visitsEn,
  dayTeam: dayTeamEn,
  dailySheet: dailySheetEn,
  gdprPage: gdprPageEn,
  dict: dictEn,
  adm: admEn,
  aud: audEn,
  lhist: lhistEn,
  alerts: alertsEn,
  notif: notifEn,
  notifs: notifsEn,
  appu: appuEn,
  fleet: { web: { ...fleetWebEn, ...fleetReportsEn, ...fleetAnalyticsEn, ...fleetBoardEn, ...fleetKitEn, ...fleetVehiclesUiEn, ...fleetLeasesUiEn, ...fleetAnalyticsViewsEn, ...fleetRoutesEn, ...fleetAnalyticsUiEn, ...fleetLogbooksUiEn, ...fleetReviewUiEn, ...fleetChecklistsServiceUiEn, ai: fleetAiEn } },
  acct: acctEn,
  ai: aiEn,
  hero: heroEn,
  search: searchEn,
  chat: chatEn,
  heroSales: heroSalesEn,
  heroWork: heroWorkEn,
  heroBi: heroBiEn,
  heroFin: heroFinEn,
  heroAdmin: heroAdminEn,
  ops: opsEn,
  fin: finEn,
  acp: acpEn,
  lx: lxEn,
  repSales: repSalesEn,
  repSalesWeek: repSalesWeekEn,
  mbr: mbrEn,
  ins: insEn,
  erps: erpsEn,
  pln: plnEn,
  coll: collEn,
  cdoc: cdocEn,
  foreignCust: foreignCustEn,
  custLife: custLifeEn,
  costs: costsEn,
  cf: cfEn,
  pur: purEn,
  qual: qualEn,
  com: comEn,
  comov: comOvEn,
  teren: terenEn,
  wh: whEn,
  svy: svyEn,
  prc: prcEn,
  ivc: ivcEn,
  sord: sordEn,
  lv: lvEn,
  nav: {
    main: 'Main navigation',
    analyticsBoard: 'Board',
    analyticsTeam: 'Team',
    analyticsCustomers: 'Customers',
    analyticsProducts: 'Products',
    analyticsMargin: 'Margin',
    analyticsForecasts: 'Forecasts',
    analyticsReceivables: 'Receivables',
    analyticsProfitability: 'Profitability',
    collapse: 'Collapse menu',
    expand: 'Expand menu',
    openMenu: 'Open menu',
    legal: 'Legal center',
    gdpr: 'Personal data (GDPR)',
    consents: 'Legal consents',
    users: 'Users',
    roles: 'Roles & permissions',
    settings: 'Settings',
    dictionaries: 'Dictionaries',
    audit: 'Audit log',
    feedback: 'Feedback',
    help: 'Help',
  },
  common: {
    retry: 'Try again',
    back: 'Back',
    clear: 'Clear',
    close: 'Close',
    all: 'All',
    columns: 'Columns',
    rowsPerPage: 'Rows per page',
    pageOf: 'Page {page} of {pages}',
    results: '{count} results',
    sampleData: 'Sample data',
    comingSoon: 'Coming soon',
    list: 'List',
    map: 'Map',
    previous: 'Previous',
    next: 'Next',
    errorGeneric: 'Something went wrong. Please try again.',
    errorNetwork: 'Cannot reach the server.',
    errorForbidden: 'You are not allowed to do this.',
    noResults: 'No results',
    unknown: '—',
  },
  auth: {
    title: 'Sign in',
    subtitle: 'Sign in to the {client} workspace',
    signingIn: 'Signing in…',
    totpLabel: 'Verification code',
    totpHint: 'Enter the 6-digit code from your authenticator app.',
    invalidCredentials: 'Invalid email or password.',
    invalidTotp: 'Invalid verification code.',
    tooManyAttempts: 'Too many attempts. Try again shortly.',
    emailInvalid: 'Enter a valid email address.',
    passwordRequired: 'Enter your password.',
    totpFormat: 'The code has 6 digits.',
    useOtherAccount: 'Use another account',
    sessionExpired: 'Your session has expired. Please sign in again.',
    idleLoggedOut: 'Signed out due to inactivity.',
    idleTitle: 'Your session is about to expire',
    idleBody: 'For security reasons you will be signed out in {seconds} s.',
    stayLoggedIn: 'Stay signed in',
    logoutNow: 'Sign out now',
    poweredBy: 'NEXOPS platform',
    heading: 'Sign in to the system',
    headingSub: 'Enter your work credentials to access the {client} workspace.',
    emailLabel: 'Work ID',
    emailPlaceholder: 'jane.doe@company.com',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Enter your password',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    remember: 'Remember my e-mail on this device',
    submit: 'Sign in to the system',
    forgot: 'Forgot your password?',
    forgotBody: 'Passwords are reset by your organization administrator. Contact support with your work e-mail and we will set a new one.',
    forgotAction: 'Request a password reset',
    capsLock: 'Caps Lock is on',
    needHelp: 'Need help?',
    writeToUs: 'Write to us',
    callSupport: 'Call support',
    supportHours: 'Mon–Fri 7:00–18:00',
    secureTitle: 'Your data is safe.',
    secureBody: 'We use TLS encryption and every action is recorded in the audit log.',
    systemName: 'NEXOPS — Business Operations Platform',
  },
  demo: {
    mode: 'Demo mode',
    switchAs: 'Sign in as…',
    hint: 'Switch roles to see how data scope changes.',
    switched: 'Switched to: {name}',
    current: 'Current',
  },
  dataScope: {
    OWN: 'Own records',
    TEAM: 'Team',
    ALL: 'Whole organization',
  },
  topbar: {
    search: 'Search',
    searchPlaceholder: 'Search customers, leads, tasks, requests…',
    pendingApprovals: 'awaiting approval',
    returnedApprovals: 'to fix',
    help: 'Help and shortcuts',
    helpIntro: 'Keyboard shortcuts available throughout the app.',
    helpSearch: 'Search',
    helpAssistant: 'AI assistant',
    searchSoon: 'Global search arrives in the next milestone (M2).',
    notifications: 'Notifications',
    noNotifications: 'No new notifications',
    userMenu: 'User menu',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeSystem: 'System',
  },
  errors: {
    forbiddenTitle: 'No access',
    forbiddenBody: 'Your role is not allowed to open this view.',
    notFoundTitle: 'Page not found',
    notFoundBody: 'The page you are looking for does not exist or has moved.',
    goHome: 'Back to My day',
  },
  placeholder: {
    title: 'In progress — {milestone}',
    body: 'This module is being built. Navigation and permissions already work.',
  },
  pages: {
    leads: 'New customer acquisition pipeline (kanban).',
    visits: 'Sales visit plan and history.',
    tasks: 'Tasks and automatic follow-up contacts.',
    workflow: 'Approval processes: new customer, prices, credit limits.',
    analytics: 'Sales, customer and margin analytics.',
    analyticsBoard: 'Board dashboard: sales, margin, trends.',
    analyticsTeam: 'Team performance and target attainment.',
    analyticsCustomers: 'ABC / Pareto, RFM, lost and recovered customers.',
    analyticsProducts: 'Product analysis, cross-sell, white space.',
    analyticsMargin: 'Margin, corrections, waterfall.',
    analyticsForecasts: 'Sales forecasts and targets.',
    analyticsReceivables: 'Receivables and ageing.',
    analyticsProfitability: 'Customer and route profitability.',
    logistics: 'Deliveries, routes and transport costs.',
    admin: 'Users, roles, organization settings, audit.',
    legal: 'Legal documents, terms and policies.',
    gdpr: 'Personal data export and erasure (GDPR).',
    consents: 'Versions and acceptance of legal consents.',
    users: 'Organization user accounts.',
    roles: 'Roles, permissions and data scopes.',
    settings: 'Organization settings.',
    dictionaries: 'Choice lists used in forms (e.g. competitor suppliers).',
    audit: 'Audit event log.',
    feedback: 'Bug and suggestion reports.',
    help: 'Guides and changelog.',
  },
  customers: {
    subtitle: 'Customer records synced from the ERP.',
    searchPlaceholder: 'Search by name, tax ID, city…',
    emptyTitle: 'No customers',
    emptyBody: 'No customers match the current criteria.',
    mapMissingCoords: '{count} customers on this result page have no coordinates',
    mapLimited: 'Showing {shown} of {total} customers on the map — narrow the search or filters.',
    unassigned: 'Unassigned',
    col: {
      name: 'Customer',
      nip: 'Tax ID',
      city: 'City',
      status: 'Status',
      segment: 'Segment',
      customerClass: 'Class',
      owner: 'Owner',
      lastOrderAt: 'Last order',
      lastContactAt: 'Last contact',
      nextContactAt: 'Next contact',
    },
    detail: {
      backToList: 'Customers',
      address: 'Address',
      owner: 'Owner',
      nip: 'Tax ID',
      segment: 'Segment',
      lastContact: 'Last contact',
      nextContact: 'Next contact',
      notFound: 'Customer not found or you do not have access to it.',
      tabSales: 'Sales',
      tabProducts: 'Products',
      tabVisits: 'Visits',
      tabDocuments: 'Documents',
      tabAlerts: 'Alerts',
      tabPlaceholder: 'The “{tab}” section arrives in milestone M2/M3.',
    },
  },
  myDay: myDayEn,
};
